export const LEGACY_GUIDE_OVERVIEW = {
  title: "Operação do evento — Plataforma de eventos",
  intro: "Prepare o evento no Painel, valide a experiência do público e opere o dia sem precisar conhecer a implementação interna.",
  journey: [
    "Defina evento, equipe, participantes e regras de cadastro.",
    "Configure canais, login, inscrições e credenciamento.",
    "Monte identidade, página inicial, conteúdo e navegação.",
    "Prepare networking, social, atividades, A&B e transmissões.",
    "Execute checklist, abra o evento, acompanhe sinais e preserve resultados.",
  ],
  quickTasks: [
    ["Publicar ou compartilhar um link", "Acessos, inscrições e credenciamento"],
    ["Adicionar ou importar pessoas", "Evento, equipe e participantes"],
    ["Organizar menu, marca e conteúdo", "Experiência, conteúdo e navegação"],
    ["Preparar QR, atividade ou transmissão", "Engajamento, social e transmissões"],
    ["Abrir, acompanhar ou encerrar o evento", "Operação, comunicação e resultados"],
  ],
  scope: [
    "Este guia cobre somente eventos da Plataforma de eventos: configuração no Painel, aplicativo do participante, áreas de gestor e expositor, credenciamento associado, comunicação, relatórios e integrações operadas dentro do evento.",
    "“Painel” é onde a equipe configura e acompanha; “aplicativo” é a experiência de participantes, gestores, expositores e convidados; “Credenciamento” é a estação de registro, check-in e etiqueta.",
  ],
};

const legacySourcePages = [
  {
    id: "overview",
    title: "Início",
    group: "Comece aqui",
    intro: "Um único guia para preparar, publicar, operar e medir eventos da Plataforma 2.",
    topics: ["Pilares do guia", "Jornada recomendada", "Atalhos por tarefa", "Escopo"],
  },
  {
    id: "access",
    title: "Acessos, inscrições e credenciamento",
    group: "Preparar",
    intro: "Defina por onde cada público entra e quais condições liberam cada canal.",
    topics: [
      {
        title: "Canais e links",
        path: "Eventos → selecione o evento → Links ou Compartilhar",
        paragraphs: [
          "A Plataforma 2 pode expor três destinos operacionais: o aplicativo do participante, o acesso do credenciamento e o formulário público de registro do credenciamento.",
        ],
        table: {
          headers: ["Canal", "Quando aparece", "Quando pode ser usado"],
          rows: [
            ["Aplicativo do participante", "Networking habilitado", "Evento em andamento"],
            ["Credenciamento", "Credenciamento habilitado", "Evento em andamento"],
            ["Formulário de registro", "Credenciamento habilitado", "Inscrições abertas"],
          ],
        },
        callouts: [
          {
            title: "Compartilhe o destino certo.",
            text: "O link do aplicativo não substitui o acesso do credenciamento, e o formulário de registro não desbloqueia a estação de check-in.",
          },
        ],
        validation: [
          "Abra cada link em uma janela anônima.",
          "Confirme que título, identidade visual e evento são os esperados.",
          "Teste o link com um perfil real de cada público que será atendido.",
        ],
      },
      {
        title: "Disponibilidade do aplicativo",
        path: "Editar evento → Status do evento e Módulos",
        paragraphs: [
          "O aplicativo do participante só fica disponível quando o evento está em andamento e Networking está habilitado. Networking funciona como a chave principal de publicação da experiência da Plataforma 2.",
        ],
        table: {
          headers: ["Situação", "Efeito para o público"],
          rows: [
            ["Rascunho ou Não iniciado", "Aplicativo indisponível"],
            ["Em andamento", "Aplicativo disponível, desde que Networking esteja habilitado"],
            ["Parado", "Acesso temporariamente bloqueado"],
            ["Encerrado", "Acesso encerrado"],
          ],
        },
        callouts: [
          {
            variant: "warning",
            title: "Ao reativar, revise as datas.",
            text: "A reativação recalcula datas de término do evento e das inscrições. Confira os dois campos antes de salvar e divulgar novamente.",
          },
        ],
        validation: ["Confirme o status no card do evento e abra o link do aplicativo fora da sessão do Painel."],
      },
      {
        title: "Inscrições",
        path: "Editar evento → Definições de registro",
        paragraphs: [
          "A situação das inscrições é uma regra global. O formulário dentro do aplicativo também exige a opção “Habilitar formulário de registro” na configuração de Login; o formulário do credenciamento exige o módulo de Credenciamento.",
        ],
        steps: [
          "Defina a janela de início e término.",
          "Monte os campos do participante e marque os obrigatórios.",
          "Configure consentimento e link da política de privacidade quando aplicável.",
          "Defina limite de inscrições e a mensagem exibida quando a capacidade for atingida.",
          "Restrinja domínios de e-mail somente quando essa regra fizer parte da política do evento.",
          "Escolha e teste o modelo de e-mail automático de confirmação.",
        ],
        callouts: [
          {
            title: "Datas, situação e limite trabalham juntos.",
            text: "Uma janela válida não abre um formulário que foi fechado manualmente. Quando o limite está ativo e é atingido, o cadastro público é bloqueado e a mensagem configurada é mostrada.",
          },
        ],
        validation: [
          "Faça um cadastro de teste com um e-mail permitido.",
          "Tente repetir o e-mail e valide o bloqueio de duplicidade.",
          "Confira a chegada do e-mail de confirmação e o acesso após o cadastro.",
        ],
      },
      {
        title: "Login e convidados",
        path: "Editar evento → Networking → Login",
        paragraphs: [
          "Escolha o método de entrada de acordo com a base de participantes e o nível de controle desejado.",
        ],
        table: {
          headers: ["Método", "Uso recomendado"],
          rows: [
            ["Código de acesso único", "Maior controle; o código enviado por e-mail tem uso único"],
            ["Código de acesso fixo", "O mesmo código pode ser reutilizado pelo participante"],
            ["Somente e-mail pré-registrado", "Entrada rápida para uma base já carregada e validada"],
            ["Convidado", "Acesso limitado a conteúdo liberado, sem recursos exclusivos de participantes"],
          ],
        },
        callouts: [
          {
            variant: "warning",
            title: "Convidado não é participante.",
            text: "Networking, atividades e pontos de interesse permanecem exclusivos de participantes. Use convidado para consulta de conteúdo, não para jornadas que precisam de identidade e histórico.",
          },
        ],
        validation: ["Teste um participante, um gestor, um expositor e um convidado em sessões separadas."],
      },
      {
        title: "Credenciamento",
        path: "Editar evento → Credenciamento",
        paragraphs: [
          "O Credenciamento é o canal da equipe de recepção para localizar participantes, registrar presença e imprimir etiquetas. Ele possui código de acesso e identidade visual próprios.",
        ],
        steps: [
          "Defina o código de acesso da equipe.",
          "Escolha busca por nome/e-mail, leitura de QR Code e a opção inicial da estação. O RFID depende de um campo de participante marcado como RFID.",
          "Selecione os campos usados na busca e os dados destacados após a identificação.",
          "Configure a mensagem de check-in duplicado e o tempo de retorno automático à tela inicial.",
          "Revise o modelo de etiqueta antes de conectar a impressora.",
        ],
        validation: [
          "Faça um primeiro check-in e depois repita para conferir o aviso de duplicidade.",
          "Imprima uma etiqueta de teste com dados longos e campos vazios.",
          "Confirme o registro na área de Relatórios do evento.",
        ],
      },
      {
        title: "Sympla",
        path: "Editar evento → Módulos → Sympla",
        paragraphs: [
          "Use a integração para associar um evento da Sympla, mapear os campos recebidos e manter a base de participantes sincronizada.",
        ],
        steps: [
          "Informe a chave de acesso e carregue os eventos disponíveis.",
          "Associe o evento correto.",
          "Mapeie cada campo da Sympla para um campo do formulário de participantes.",
          "Faça uma sincronização controlada e revise a amostra importada antes de automatizar.",
        ],
        callouts: [
          {
            variant: "danger",
            title: "A sincronização automática pode excluir registros.",
            text: "Quando a sincronização de dados está ativa, participantes criados no Painel que não existem na Sympla podem ser removidos. Exporte a base e valide o evento e o mapeamento antes de ativar.",
          },
        ],
        validation: ["Compare contagem, e-mails e campos de uma amostra entre Sympla, Relatórios e o aplicativo."],
      },
    ],
  },
  {
    id: "event",
    title: "Evento, equipe e participantes",
    group: "Preparar",
    intro: "Organize ciclo de vida, perfis, formulários e base de pessoas antes de abrir os canais.",
    topics: [
      {
        title: "Dados e ciclo de vida",
        path: "Editar evento → cabeçalho do formulário",
        paragraphs: [
          "Título, situação, data de término, situação das inscrições e módulos ativos determinam o que o público consegue abrir. Use Rascunho e Não iniciado para preparação, Em andamento para publicação, Parado para bloqueio temporário e Encerrado para fechamento.",
        ],
        bullets: [
          "A data de término pode encerrar o evento automaticamente; não dependa apenas de uma ação manual.",
          "Parar ou encerrar o evento bloqueia o aplicativo do participante.",
          "Reativar um evento encerrado ou parado exige uma nova validação de links, datas e inscrições.",
        ],
        validation: ["Revise o resumo do card do evento depois de salvar e atualize a lista antes de compartilhar links."],
      },
      {
        title: "Perfis e permissões",
        paragraphs: ["Cadastre cada pessoa no papel correto. O papel altera as rotas, ferramentas e dados visíveis no aplicativo."],
        table: {
          headers: ["Perfil", "Uso operacional", "Acessos principais"],
          rows: [
            ["Participante", "Público do evento", "Networking, atividades, QR e conteúdo habilitado"],
            ["Gestor", "Equipe de operação", "Área do gestor, participantes, notificações, moderação e relatórios de transmissão"],
            ["Expositor", "Equipe de estande", "Perfil do expositor e coleta de leads"],
            ["Convidado", "Consulta sem cadastro", "Somente conteúdo liberado para convidados"],
          ],
        },
        callouts: [
          {
            title: "A seção também controla acesso.",
            text: "Mesmo um perfil autorizado é redirecionado quando a seção correspondente não está configurada para o evento.",
          },
        ],
      },
      {
        title: "Participantes",
        path: "Evento → Relatórios → Registros",
        paragraphs: [
          "Consulte, filtre, exporte, crie e atualize participantes. O relatório também mostra origem, situação e, quando o Credenciamento está ativo, o histórico de check-ins.",
        ],

        bullets: [
          "Participantes pendentes ainda podem precisar concluir perfil ou tutorial.",
          "E-mails duplicados geram alerta no card do evento e devem ser resolvidos antes da abertura.",
          "Excluir um participante remove dados operacionais associados; exporte antes de qualquer exclusão em massa.",
        ],
        validation: ["Pesquise o participante pelo mesmo e-mail usado no login e confira situação, perfil e histórico."],
      },
      {
        title: "Gestores e expositores",
        path: "Definições de registro e Relatórios → Usuários",
        paragraphs: [
          "Gestores e expositores usam um formulário próprio, separado do formulário dos participantes. Um usuário expositor precisa estar associado ao expositor/estande correto para que perfil e coleta de leads funcionem.",
        ],
        callouts: [
          {
            variant: "warning",
            title: "Conta de expositor sem vínculo é incompleta.",
            text: "O card do evento alerta quando existem usuários expositores sem estande associado. Resolva os vínculos antes de testar QR Code e coleta de leads.",
          },
        ],
        validation: ["Entre com uma conta de gestor e uma de expositor; confirme menus, estande e permissões."],
      },
      {
        title: "Formulários e importação",
        path: "Editar evento → Definições de registro → Adicionar registros",
        steps: [
          "Finalize os campos do formulário antes de gerar a planilha-modelo.",
          "Baixe o modelo e preserve os títulos das colunas.",
          "Carregue o arquivo e corrija todos os erros exibidos.",
          "Revise a prévia da tabela antes de salvar o evento.",
          "Atualize Relatórios e compare a quantidade importada.",
        ],
        callouts: [
          {
            variant: "danger",
            title: "Não ignore validações em operação normal.",
            text: "A opção pode permitir dados incompatíveis com o formulário e comprometer login, relatórios e módulos que dependem do cadastro.",
          },
        ],
        validation: ["Teste login e edição de perfil com pelo menos um participante importado."],
      },
      {
        title: "Alertas de cadastro",
        paragraphs: [
          "O card do evento reúne alertas acionáveis: capacidade a partir de 80%, e-mails duplicados, expositores sem vínculo, transmissões sem responsável, erros aguardando revisão e configuração ainda não finalizada.",
        ],
        validation: ["Abra o indicador de alertas e elimine ou justifique cada item antes da abertura."],
      },
    ],
  },
  {
    id: "experience",
    title: "Experiência, conteúdo e navegação",
    group: "Preparar",
    intro: "Construa a experiência que o participante realmente verá: marca, página inicial, seções e ordem de navegação.",
    topics: [
      {
        title: "Identidade visual e PWA",
        path: "Editar evento → Networking → Customização",
        paragraphs: [
          "Use a customização simplificada para definir tema claro/escuro e cor principal com consistência. Recorra ao modo avançado quando precisar controlar fundos, contêineres, cards, navegação, modais e tela de carregamento.",
        ],
        bullets: [
          "Logo principal e logo da barra de navegação têm usos diferentes.",
          "Ícone do aplicativo, imagem de compartilhamento, título e descrição afetam instalação e compartilhamento.",
          "Verifique contraste de texto, botões e bordas em todas as cores escolhidas.",
        ],
        validation: [
          "Abra o aplicativo em celular e desktop.",
          "Teste instalação na tela inicial e prévia de compartilhamento.",
          "Confirme legibilidade em login, cards, menus e modais.",
        ],
      },
      {
        title: "Página inicial",
        path: "Networking → Gerenciamento de componentes → Página inicial",
        paragraphs: [
          "A página inicial reúne banners e conteúdo editorial. Use-a para orientar o participante para as próximas ações, sem repetir todo o menu.",
        ],
        bullets: [
          "Priorize horários, locais, chamada para agenda e informações de suporte.",
          "Use banners com proporção consistente e texto legível em telas pequenas.",
          "Mantenha links e chamadas coerentes com as seções realmente habilitadas.",
        ],
        validation: ["Acesse cada chamada da página inicial e confirme o destino com os perfis permitidos."],
      },
      {
        title: "Estrutura do aplicativo",
        path: "Networking → Gerenciamento de componentes",
        paragraphs: ["Apresente as seções pela finalidade operacional, não como uma lista técnica."],
        table: {
          headers: ["Grupo", "Seções"],
          rows: [
            ["Conteúdo", "Página inicial, Agenda, Palestrantes, Patrocinadores e Páginas personalizadas"],
            ["Relacionamento e negócio", "Networking, Expositores e Coleta de lead"],
            ["Engajamento", "Atividades, Pontos de interesse, Galeria e Social"],
            ["Serviços ao participante", "Comunicados e A&B"],
          ],
        },
        callouts: [
          {
            title: "Social é uma única seção.",
            text: "Ao incluir Social, o aplicativo oferece Feed e Conversas. A permissão para criar grupos é configurada separadamente.",
          },
        ],
      },
      {
        title: "Ordem, rodapé e menu",
        path: "Networking → Gerenciamento de componentes e Configurações",
        bullets: [
          "Arraste os cards para definir a ordem de navegação.",
          "Marque somente os destinos prioritários para o rodapé.",
          "Use as opções de Menu, QR Code e ícone do usuário de forma consistente com o desenho do evento.",
          "Remover uma seção também impede o acesso normal à rota correspondente.",
        ],
        validation: ["Percorra menu e rodapé com cada perfil e confirme ordem, rótulo, ícone e destino."],
      },
      {
        title: "Conteúdo base",
        paragraphs: [
          "Agenda, palestrantes, expositores e patrocinadores se relacionam. Cadastre primeiro as entidades e depois faça as associações necessárias nas sessões e nos canais.",
        ],
        steps: [
          "Cadastre categorias e níveis quando o componente exigir organização.",
          "Inclua imagens, descrições e links finais.",
          "Associe palestrantes, atividades, arquivos e transmissões à agenda.",
          "Associe usuários expositores aos estandes.",
          "Revise páginas personalizadas e links externos.",
        ],
        validation: ["Abra listas e detalhes; procure registros sem imagem, associação, data ou link."],
      },
      {
        title: "Validação da experiência",
        steps: [
          "Use uma janela anônima e um dispositivo móvel real.",
          "Teste login, tutorial, página inicial, menu, rodapé e saída da conta.",
          "Repita com participante, gestor, expositor e convidado.",
          "Desative a rede por alguns instantes e confirme a mensagem de modo offline.",
          "Retorne ao Painel para corrigir qualquer seção que apareça vazia ou inacessível.",
        ],
      },
    ],
  },
  {
    id: "engagement",
    title: "Engajamento, social e transmissões",
    group: "Preparar",
    intro: "Configure as jornadas que geram interação, presença, negócio e participação ao vivo.",
    topics: [
      {
        title: "Networking e QR Code",
        path: "Networking → componentes e Configurações",
        paragraphs: [
          "Networking publica o aplicativo e, para participantes, libera diretório, conexões e jornadas por QR Code. O QR também pode registrar visita a expositor, ponto de interesse ou lead, conforme o contexto da leitura.",
        ],
        bullets: [
          "Use o modo somente QR quando conexões presenciais forem obrigatórias.",
          "Não esconda o QR do usuário se a jornada depender de troca de contatos.",
          "Teste QR Codes impressos com iluminação e distância semelhantes às do local.",
        ],
        validation: ["Faça uma conexão comum, uma conexão por QR e uma coleta comercial de teste."],
      },
      {
        title: "Agenda e transmissões",
        path: "Networking → Agenda → sessão → Transmissão",
        steps: [
          "Cadastre data, horário, descrição, palestrantes, atividades, arquivos e regra de vagas da sessão.",
          "Crie o canal, defina início e término e associe um responsável.",
          "Cadastre e ordene as fontes de vídeo; mantenha uma alternativa pronta quando possível.",
          "Defina chat, mensagens, enquetes e controles de moderação.",
          "Abra o canal como público, gestor e responsável antes do evento.",
        ],
        callouts: [
          {
            variant: "warning",
            title: "Toda transmissão precisa de responsável.",
            text: "O responsável controla fontes e interações do canal. Transmissões sem responsável aparecem como alerta no card do evento.",
          },
          {
            title: "O canal segue seu ciclo próprio.",
            text: "A programação pode iniciar e encerrar o canal; reabrir ou resetar um canal fechado altera dados de uso e deve ser tratado como ação excepcional.",
          },
        ],
        validation: ["Confirme reprodução, troca de fonte, chat, reação, enquete, contador de público e link direto."],
      },
      {
        title: "Social e conversas",
        path: "Networking → Social",
        paragraphs: [
          "A seção Social oferece Feed e Conversas para usuários registrados. Conversas diretas, grupos e chat de transmissão possuem regras próprias; habilitar grupos não substitui a configuração do chat de cada transmissão.",
        ],
        bullets: [
          "Defina antes do evento quem acompanhará denúncias e conteúdo impróprio.",
          "Fotos aprovadas em atividades podem ser projetadas no Feed quando a publicação automática estiver habilitada.",
          "Teste publicação, comentário, curtida, conversa direta e grupo com contas diferentes.",
        ],
        validation: ["Confirme que exclusões, ocultações e denúncias refletem para as contas afetadas."],
      },
      {
        title: "Atividades e moderação",

        path: "Networking → Atividades e Área do gestor → Atividades",
        table: {
          headers: ["Tipo", "Operação"],
          rows: [
            ["Quiz", "Questões, respostas, dicas e regra de conclusão"],
            ["Foto por participação", "Envio, aprovação, rejeição e eventual publicação no Feed"],
            ["Competição de fotos", "Moderação, votação, encerramento e reabertura controlada"],
          ],
        },
        callouts: [
          {
            variant: "warning",
            title: "Não finalize com fotos pendentes.",
            text: "A competição só pode ser finalizada depois que todas as fotos forem aprovadas ou rejeitadas. O gestor pode aprovar, rejeitar e reverter decisões.",
          },
        ],
        validation: ["Conclua uma atividade de cada tipo e confira histórico e projeção social."],
      },
      {
        title: "A&B",
        path: "Networking → A&B e Área do gestor → A&B",
        paragraphs: [
          "Cadastre banners, itens, categorias e mesas; depois valide a jornada de pedido e a atualização de status pela equipe.",
        ],
        callouts: [
          {
            title: "Pedidos antigos não ficam abertos indefinidamente.",
            text: "Pedidos pendentes de dias anteriores são encerrados automaticamente. No início de cada dia, confira o histórico antes de abrir o atendimento.",
          },
        ],
        validation: ["Faça um pedido, atualize cada status e confirme a visão do participante e do gestor."],
      },
      {
        title: "Galeria, pontos e leads",
        paragraphs: [
          "Galeria organiza imagens do evento; pontos de interesse criam uma jornada de coleta por QR; coleta de leads registra contatos para gestores e expositores autorizados.",
        ],
        bullets: [
          "Ative somente pontos de interesse que estarão disponíveis ao público.",
          "Valide a associação de cada expositor antes de coletar leads.",
          "Exporte uma coleta de teste e confira os campos configurados no formulário.",
        ],
      },
    ],
  },
  {
    id: "operations",
    title: "Operação, comunicação e resultados",
    group: "Operar",
    intro: "Use checklists e sinais do Painel para abrir o evento com segurança, acompanhar o dia e encerrar com dados preservados.",
    topics: [
      {
        title: "Checklist pré-evento",
        checklist: [
          "Título, datas, fuso e situação revisados.",
          "Inscrições, limite, consentimento e e-mail automático testados.",
          "Links do aplicativo, credenciamento e registro validados em janela anônima.",
          "Contas de participante, gestor e expositor testadas.",
          "Expositores vinculados e transmissões com responsável.",
          "Menu, rodapé, página inicial e componentes revisados no celular.",
          "QR Codes, leitores, impressoras e etiquetas testados no local.",
          "Fontes de transmissão, chat e enquetes ensaiados.",
          "Responsáveis definidos para credenciamento, moderação, comunicação, A&B e transmissão.",
          "Alertas do card do evento revisados e base exportada como contingência.",
        ],
      },
      {
        title: "Abertura do evento",
        steps: [
          "Coloque o evento em andamento e atualize a lista de eventos.",
          "Abra ou mantenha fechadas as inscrições conforme o plano operacional.",
          "Confirme o aplicativo com uma conta de teste fora do Painel.",
          "Desbloqueie as estações de credenciamento e faça um check-in controlado.",
          "Confirme agenda, comunicados, A&B e canais de transmissão do dia.",
          "Revise alertas e deixe as telas de Relatórios e operação prontas.",
        ],
        validation: ["O público correto entra, vê a programação do dia e conclui a primeira ação sem intervenção técnica."],
      },
      {
        title: "Durante o evento",
        checklist: [
          "Acompanhar cadastros, participantes ativos e check-ins.",
          "Tratar e-mails duplicados e expositores sem vínculo.",
          "Moderar fotos, denúncias e conversas conforme a política do evento.",
          "Acompanhar transmissões, responsáveis, fontes e audiência ao vivo.",
          "Atualizar pedidos de A&B e responder incidentes de credenciamento.",
          "Enviar comunicados somente após revisar público, texto, imagem e destino.",
          "Registrar o horário e o contexto de falhas antes de consultar os logs.",
        ],
      },
      {
        title: "Comunicados e notificações",
        path: "Networking → Comunicados e Área do gestor → Notificações",
        paragraphs: [
          "Comunicados são ações diretas da operação. Lembretes de agenda e de transmissão são gerados para participantes que demonstraram interesse quando o início está próximo.",
        ],
        callouts: [
          {
            title: "Notificação no aplicativo e Web Push não são a mesma confirmação.",
            text: "O recebimento fora do aplicativo depende da permissão do navegador e da aceitação do provedor. Valide a caixa de notificações do aplicativo mesmo quando o push não aparecer.",
          },
        ],
        validation: ["Envie uma mensagem de teste a um grupo controlado e confira caixa, push, imagem e destino."],
      },
      {
        title: "E-mails",
        path: "Evento → Visualizar e-mails",
        steps: [
          "Crie ou revise o modelo e envie um teste.",
          "Confirme remetente, assunto, variáveis, QR Code e tela de confirmação quando usados.",
          "Escolha lote, envio individual ou e-mail automático de registro.",
          "Revise filtros e quantidade de destinatários antes de agendar.",
          "Acompanhe pendentes, enviados, entregues, abertos, devolvidos, reclamações e falhas.",
          "Revise a lista de supressões antes de tentar um reenvio.",
        ],
        callouts: [
          {
            variant: "warning",
            title: "O conteúdo do lote fica congelado no envio.",
            text: "Alterar o modelo depois não muda e-mails já enfileirados. Cancele e recrie o lote quando precisar corrigir o conteúdo pendente.",
          },
        ],
      },
      {
        title: "Credenciamento no local",
        steps: [
          "Mantenha uma estação de contingência e material de etiqueta disponível.",
          "Confirme o participante por busca ou QR; quando houver um campo RFID configurado, use a leitura RFID conforme a estação.",
          "Leia o aviso de duplicidade antes de repetir um check-in.",
          "Valide os dados destacados e imprima a etiqueta.",
          "Aguarde o retorno automático à tela inicial antes do próximo atendimento.",
          "Acompanhe presentes, ausentes, múltiplos check-ins e retorno diário em Relatórios.",
        ],
      },
      {
        title: "Encerramento",
        checklist: [
          "Resolver fotos pendentes e finalizar competições quando aplicável.",
          "Encerrar transmissões e salvar os relatórios de cada canal.",
          "Concluir ou revisar pedidos de A&B.",
          "Exportar participantes, usuários, check-ins, leads e dados necessários.",
          "Fechar inscrições.",
          "Encerrar o evento e confirmar que o link público foi bloqueado.",
          "Preservar evidências antes de usar qualquer exclusão ou reset.",
        ],
      },
      {
        title: "Relatórios e analytics",
        path: "Evento → Visualizar relatórios",
        paragraphs: [
          "A área de Relatórios reúne evolução de registros, participantes, gestores/expositores, check-ins e analytics do aplicativo. Transmissões possuem relatório próprio e não entram no dashboard geral.",
        ],
        table: {
          headers: ["Leitura", "O que representa"],
          rows: [
            ["Participantes", "Pessoas cadastradas e seus dados operacionais"],
            ["Check-ins", "Registros de presença; uma pessoa pode ter mais de um"],
            ["Sessões do aplicativo", "Conexões em primeiro plano, não pessoas únicas"],
            ["Visitantes estimados", "Estimativa por dispositivo/sessão de convidado"],
            ["Analytics geral", "Público, networking, conteúdo, entrada, comunicação, QR, patrocinadores e navegação"],
            ["Relatório de transmissão", "Audiência, presença conectada, reações, chat, enquetes e atividade do canal"],
          ],
        },
        callouts: [
          {
            variant: "warning",
            title: "Não some sessões como pessoas.",
            text: "Uma pessoa pode abrir mais de uma sessão. Para público identificado, use participantes; para uso do aplicativo, use sessões e visitantes estimados com o rótulo correto.",
          },
          {
            variant: "danger",
            title: "Reset é uma ação destrutiva.",
            text: "O reset do analytics geral não apaga relatórios de transmissão, participantes ou check-ins. O reset de um canal atua em outro conjunto de dados. Exporte e confirme o alvo antes de executar.",
          },
        ],
      },
      {
        title: "Alertas e diagnóstico",
        path: "Card do evento → alertas e Visualizar logs",
        steps: [
          "Comece pelo alerta do card e identifique a área afetada.",
          "Reproduza com o mesmo perfil, link, horário e dispositivo.",
          "Consulte os logs do evento e filtre pelo período e pela função afetada.",
          "Verifique se o problema é regra de acesso, conteúdo ausente, permissão, dado inválido ou falha registrada.",
          "Após corrigir, repita a validação fora da sessão do Painel.",
        ],

        callouts: [
          {
            title: "O operador precisa do efeito, não da infraestrutura.",
            text: "Registre o que o usuário tentou fazer, o resultado esperado, o resultado obtido e o horário. Esses dados tornam o diagnóstico acionável sem expor detalhes internos no guia.",
          },
        ],
      },
    ],
  },
  {
    id: "rules",
    title: "Regras que alteram a operação",
    group: "Referência",
    intro: "Resumo das condições que mais frequentemente explicam um acesso, ação ou número inesperado.",
    topics: [
      {
        title: "Acesso e publicação",
        table: {
          headers: ["Condição", "Efeito", "Ação do operador"],
          rows: [
            ["Evento fora de Em andamento", "Aplicativo bloqueado", "Revisar situação e datas"],
            ["Networking desabilitado", "Aplicativo do participante indisponível", "Habilitar somente quando a experiência estiver pronta"],
            ["Seção removida", "Rota deixa de estar disponível", "Reincluir e revisar ordem/perfis"],
            ["Perfil incompatível", "Usuário não vê a função", "Corrigir papel ou usar a conta correta"],
          ],
        },
      },
      {
        title: "Cadastro e comunicação",
        table: {
          headers: ["Condição", "Efeito", "Ação do operador"],
          rows: [
            ["Inscrições fechadas", "Cadastro público bloqueado", "Abrir manualmente ou revisar janela"],
            ["Limite atingido", "Mensagem de capacidade exibida", "Aumentar limite somente com autorização"],
            ["Domínio não permitido", "E-mail recusado", "Corrigir lista ou orientar o participante"],
            ["E-mail suprimido", "Novo envio não é entregue", "Revisar motivo antes de remover a supressão"],
          ],
        },
      },
      {
        title: "Conteúdo e engajamento",
        table: {
          headers: ["Condição", "Efeito", "Ação do operador"],
          rows: [
            ["Foto pendente", "Competição não pode ser finalizada", "Aprovar ou rejeitar todas as pendências"],
            ["Grupo desabilitado", "Não impede conversas diretas ou chat de transmissão", "Validar cada modalidade separadamente"],
            ["Pedido de A&B de dia anterior", "Pode ser encerrado automaticamente", "Conferir histórico no início do turno"],
          ],
        },
      },
      {
        title: "Métricas e resets",
        table: {
          headers: ["Regra", "Leitura correta"],
          rows: [
            ["Sessão não é pessoa", "Use participantes para pessoas e sessões para uso do aplicativo"],
            ["Analytics geral não inclui transmissão", "Abra o relatório do canal"],
            ["Reset geral e reset de transmissão são separados", "Exporte e confirme o alvo antes da ação"],
          ],
        },
      },
    ],
  },
];


const replaceCommercialNames = (value) => {
  if (typeof value === "string") {
    return value
      .replace(/Plataforma 02/g, "Plataforma de eventos")
      .replace(/Plataforma 2/g, "Plataforma de eventos")
      .replace(/Plataforma 01/g, "Plataforma de aplicativos")
      .replace(/Plataforma 1/g, "Plataforma de aplicativos");
  }
  if (Array.isArray(value)) return value.map(replaceCommercialNames);
  if (value && typeof value === "object") {
    return Object.entries(value).reduce((result, [key, item]) => ({
      ...result,
      [key]: replaceCommercialNames(item),
    }), {});
  }
  return value;
};

export const LEGACY_GUIDE_PAGES = replaceCommercialNames(
  legacySourcePages.filter((page) => page.id !== "overview"),
);
